import { Box, Skeleton, Typography, Chip, List, ListItem, ListItemText, ListItemIcon, Divider } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import { Warning as WarningIcon, Inventory as InventoryIcon } from '@mui/icons-material';
import { useDashboard } from '../DashboardContext';
import { useTranslation } from 'react-i18next';
import { DashboardInfoTip } from './DashboardInfoTip';
import DashboardWidget, { DashboardSizedChart } from './DashboardWidget';
import { useChartSurface } from '../dashboardChrome';

const DEPOT_COLORS = ['#0288d1', '#2e7d32', '#7b1fa2', '#ed6c02', '#d32f2f', '#00695c', '#c2185b', '#f57c00'];

const StockByDepotChart = () => {
  const { inventoryByDepot, currency } = useDashboard();
  const { t } = useTranslation();
  const chrome = useChartSurface();

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toFixed(0);
  };

  const formatTooltipValue = (value: number) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value) + ` ${currency}`;
  };

  if (inventoryByDepot.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
        <Typography>{t('common:dashboard.noData')}</Typography>
      </Box>
    );
  }

  return (
    <DashboardSizedChart height={300}>
      <BarChart data={inventoryByDepot} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={chrome.grid} />
        <XAxis dataKey="depot" fontSize={11} stroke={chrome.axis} />
        <YAxis tickFormatter={formatCurrency} fontSize={11} stroke={chrome.axis} />
        <Tooltip
          formatter={(value: number, name: string) => [
            formatTooltipValue(value),
            name === 'totalValue' ? t('common:dashboard.inventory.value') : name,
          ]}
          contentStyle={chrome.tooltip}
        />
        <Bar dataKey="totalValue" radius={[4, 4, 0, 0]} barSize={40}>
          {inventoryByDepot.map((_, idx) => (
            <Cell key={idx} fill={DEPOT_COLORS[idx % DEPOT_COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </DashboardSizedChart>
  );
};

const InventoryOverview = () => {
  const { inventoryByDepot, sectionLoading } = useDashboard();
  const { t } = useTranslation();
  const totalLowStock = inventoryByDepot.reduce((sum, d) => sum + d.lowStockCount, 0);

  if (sectionLoading.inventory) {
    return (
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <DashboardWidget chartHeight={300} sx={{ flex: 2 }}>
          <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
        </DashboardWidget>
        <DashboardWidget chartHeight={300} sx={{ flex: 1 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="text" width="100%" height={48} sx={{ mt: 1 }} />
          ))}
        </DashboardWidget>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
      <DashboardWidget
        sx={{ flex: 2 }}
        title={t('common:dashboard.inventory.stockByDepot')}
        helpKey="inventory.stockByDepot"
        chartHeight={300}
      >
        <StockByDepotChart />
      </DashboardWidget>

      <DashboardWidget
        sx={{ flex: 1 }}
        chartHeight={290}
        scrollable
        header={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('common:dashboard.inventory.lowStockAlerts')}
            </Typography>
            <DashboardInfoTip helpKey="inventory.lowStockAlerts" />
            {totalLowStock > 0 && (
              <Chip label={totalLowStock} size="small" color="warning" />
            )}
          </Box>
        }
      >
        {inventoryByDepot.length > 0 ? (
          <List dense>
            {inventoryByDepot.map((depot, idx) => (
              <Box key={depot.depotId}>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {depot.lowStockCount > 0 ? (
                      <WarningIcon sx={{ color: '#ed6c02', fontSize: 20 }} />
                    ) : (
                      <InventoryIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    // MUI 7 still wraps secondary in <p> by default; Chip is a <div>.
                    // disableTypography avoids invalid <p><div> nesting entirely.
                    disableTypography
                    primary={
                      <Typography variant="body1" component="div" sx={{ fontWeight: 500 }}>
                        {depot.depot}
                      </Typography>
                    }
                    secondary={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 1,
                          mt: 0.25,
                        }}
                      >
                        <Typography variant="caption" component="span" color="text.secondary">
                          {depot.totalItems} {t('common:dashboard.inventory.items')}
                        </Typography>
                        {depot.lowStockCount > 0 && (
                          <Chip
                            label={`${depot.lowStockCount} ${t('common:dashboard.inventory.low')}`}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {idx < inventoryByDepot.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
            <Typography>{t('common:dashboard.noData')}</Typography>
          </Box>
        )}
      </DashboardWidget>
    </Box>
  );
};

export default InventoryOverview;